import { Alert, Card, Form, Input, Space, Tag, Typography } from "antd";
import type { Page, ShippingRequirementPlat } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useNavigate } from "react-router";
import { Button, Pagination as ZhPagination, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { FREIGHT_RATE_TYPE, PLAT_SHIP_NEED_STATE } from "@/meta/logistics/shipNeed";
import { DEMAND_LEVEL } from "@zhihaoscm/meta";

export interface PlatformShipNeedQuery {
    page: number;
    size: number;
    id?: string;
    state?: number;
}

export interface PlatformShipNeedRow {
    plat: Partial<ShippingRequirementPlat> & { id: string | number };
}

export interface PlatformShipNeedPageProps {
    loadNeeds?: (query: PlatformShipNeedQuery) => Promise<Page<PlatformShipNeedRow>>;
}

/** 复用 Vue 平台船运需求分页接口，保持历史 URL 和查询参数。 */
export async function loadPlatformShipNeeds(query: PlatformShipNeedQuery): Promise<Page<PlatformShipNeedRow>> {
    const response = await shippingRequestHttp.service.get<Page<PlatformShipNeedRow>>("/shipping/plat/paging", {
        params: query
    });
    return response.data;
}

// 沿用 Vue currentPlatState 的默认值，首屏只查询已发布需求。
const defaultQuery: PlatformShipNeedQuery = { page: 1, size: 20, state: PLAT_SHIP_NEED_STATE.PUBLISH.code };

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

function dateText(value: unknown): string {
    return typeof value === "string" && value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function amountText(value: unknown): string {
    return typeof value === "number" ? value.toLocaleString("zh-CN") : text(value);
}

/** 平台船运需求列表，覆盖 Vue 首屏状态卡、查询、核心表格字段和详情入口。 */
export function PlatformShipNeedPage({ loadNeeds = loadPlatformShipNeeds }: PlatformShipNeedPageProps) {
    const navigate = useNavigate();
    const [rows, setRows] = useState<PlatformShipNeedRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<PlatformShipNeedQuery>(defaultQuery);
    const [id, setId] = useState("");
    const [state, setState] = useState<number | undefined>(PLAT_SHIP_NEED_STATE.PUBLISH.code);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadNeeds(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("平台船运需求加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadNeeds, query]);

    /** 依照旧 Vue 列表提交 id/state，避免向接口发送空筛选字段。 */
    const submitFilters = () => {
        const nextQuery: PlatformShipNeedQuery = { page: 1, size: 20 };
        if (id.trim()) nextQuery.id = id.trim();
        if (state !== undefined) nextQuery.state = state;
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setId("");
        setState(PLAT_SHIP_NEED_STATE.PUBLISH.code);
        setQuery(defaultQuery);
    };

    // 用 zhihao-ui-react 的列契约承接旧 Vue 平台船运需求 ZhTable 的字段与操作插槽。
    const needColumns = useMemo<TableColumnProps<PlatformShipNeedRow>[]>(
        () => [
            {
                key: "demandLevel",
                label: "需求等级",
                prop: "plat.demandLevel",
                render: ({ row }) => (
                    <Tag>{DEMAND_LEVEL.getIntroByCode(Number(row.plat.demandLevel)) || DEFAULT_VALUE}</Tag>
                )
            },
            {
                key: "id",
                label: "编号",
                prop: "plat.id",
                render: ({ row }) => (
                    <Link to={`/logistics/shippingNeed/platformShipNeedInfo/${row.plat.id}`}>{text(row.plat.id)}</Link>
                )
            },
            {
                key: "owner",
                label: "货主",
                prop: "plat.ownerEnterprise",
                render: ({ row }) => text(row.plat.ownerEnterprise?.name)
            },
            {
                key: "source",
                label: "始发码头",
                prop: "plat.sourcePortName",
                render: ({ row }) => text(row.plat.sourcePortName)
            },
            {
                key: "destination",
                label: "目的码头",
                prop: "plat.destinationPortName",
                render: ({ row }) => text(row.plat.destinationPortName)
            },
            {
                key: "freightTons",
                label: "运货吨数",
                prop: "plat.freightTons",
                render: ({ row }) => `${amountText(row.plat.freightTons)} 吨`
            },
            {
                key: "unitPrice",
                label: "意向单价",
                prop: "plat.unitPrice",
                render: ({ row }) =>
                    Number(row.plat.unitPriceType) === FREIGHT_RATE_TYPE.CALL_FOR_PRICE.code
                        ? FREIGHT_RATE_TYPE.CALL_FOR_PRICE.intro
                        : `${amountText(row.plat.unitPrice)} / 吨`
            },
            {
                key: "loadDate",
                label: "装载日期",
                prop: "plat.loadDate",
                render: ({ row }) => `${dateText(row.plat.loadDate)} +${text(row.plat.loadDays)}天`
            },
            {
                key: "state",
                label: "状态",
                prop: "plat.state",
                render: ({ row }) => (
                    <Tag>{PLAT_SHIP_NEED_STATE.getIntroByCode(Number(row.plat.state)) || DEFAULT_VALUE}</Tag>
                )
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Link to={`/logistics/shippingNeed/platformShipNeedInfo/${row.plat.id}`}>查看</Link>
                )
            }
        ],
        []
    );

    return (
        <section aria-label="platform-ship-need-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space wrap>
                    <Button
                        aria-label="全部需求"
                        aria-pressed={state === undefined}
                        type={state === undefined ? "primary" : "default"}
                        onClick={() => setState(undefined)}
                    >
                        全部需求
                    </Button>
                    {PLAT_SHIP_NEED_STATE.options.map(option => (
                        <Button
                            key={String(option.value)}
                            aria-label={option.label}
                            aria-pressed={state === option.value}
                            type={state === option.value ? "primary" : "default"}
                            onClick={() => setState(Number(option.value))}
                        >
                            {option.label}
                        </Button>
                    ))}
                </Space>
            </Card>
            <Card>
                <Form layout="inline" onFinish={submitFilters}>
                    <Form.Item label="需求编号或货主">
                        <Input
                            aria-label="需求编号或货主"
                            placeholder="请输入货主名称或编号搜索"
                            value={id}
                            onChange={event => setId(event.target.value)}
                        />
                    </Form.Item>
                    <Space>
                        <Button aria-label="查询需求" htmlType="submit" type="primary">
                            查询需求
                        </Button>
                        <Button aria-label="重置" onClick={resetFilters}>
                            重置
                        </Button>
                    </Space>
                </Form>
            </Card>
            <Card>
                <ZhTable<PlatformShipNeedRow>
                    rowKey={row => String(row.plat.id)}
                    data={rows}
                    columns={needColumns}
                    emptyText="暂无平台船运需求"
                    pagination={false}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4 w-full" align="center">
                                <Typography.Title className="mb-0" level={4}>
                                    平台船运需求
                                </Typography.Title>
                                <Button
                                    aria-label="新增"
                                    type="primary"
                                    onClick={() => navigate("/logistics/shippingNeed/platformShipNeedAdd")}
                                >
                                    新增
                                </Button>
                            </Space>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐旧版 Element Plus ProTable 的默认 sizes，切换条数后回到第一页。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default PlatformShipNeedPage;
